import React, { Children, createContext, isValidElement, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown } from 'lucide-react';
import { boxesEqual, useGlide } from './useGlide.js';

// Fluid Functionalism select, cut down to Base UI. The trigger is a bounded
// control (36px default, 28px compact). One highlight glides between rows.
// The popup is non-modal so the page keeps scrolling.

const SizeContext = createContext('default');

export function Select({
  value,
  defaultValue,
  onValueChange,
  disabled = false,
  name,
  required = false,
  size = 'default',
  children,
}) {
  const items = useMemo(() => collectItems(children), [children]);
  return (
    <SizeContext.Provider value={size}>
      <SelectPrimitive.Root
        value={value === '' ? null : value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next ?? '')}
        disabled={disabled}
        name={name}
        required={required}
        items={items}
        modal={false}
      >
        {children}
      </SelectPrimitive.Root>
    </SizeContext.Provider>
  );
}

export function SelectTrigger({ placeholder = 'Select…', icon: Icon, className, ...props }) {
  const size = useContext(SizeContext);
  const iconSize = size === 'compact' ? 14 : 16;
  return (
    <SelectPrimitive.Trigger
      {...props}
      className={['edl-select', className].filter(Boolean).join(' ')}
      data-size={size}
      data-icon-start={Icon ? '' : undefined}
    >
      <span className="edl-select__surface" aria-hidden="true" />
      {Icon ? <Icon size={iconSize} strokeWidth={1.5} aria-hidden="true" /> : null}
      <SelectPrimitive.Value className="edl-select__value" placeholder={placeholder} />
      <SelectPrimitive.Icon className="edl-select__chevron">
        <ChevronDown size={iconSize} strokeWidth={1.5} aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({ children, className }) {
  const size = useContext(SizeContext);
  const listObserver = useRef(null);
  const resizeObserver = useRef(null);
  const settled = useRef(false);
  const arm = useRef(0);
  const [box, setBox] = useState(null);
  const [itemKey, setItemKey] = useState(null);
  const glide = useGlide(itemKey);

  // The list unmounts while the popup is closed, so the watcher starts from
  // the ref callback. The highlight stays hidden until the highlighted row has
  // settled, then snaps there. Later row changes glide; resizes snap.
  const listRef = useCallback((node) => {
    listObserver.current?.disconnect();
    resizeObserver.current?.disconnect();
    cancelAnimationFrame(arm.current);
    listObserver.current = null;
    resizeObserver.current = null;
    if (!node) {
      settled.current = false;
      setItemKey(null);
      setBox(null);
      return;
    }
    const place = () => {
      const item = node.querySelector('.edl-select__item[data-highlighted]:not([data-disabled])')
        || node.querySelector('.edl-select__item[data-selected]:not([data-disabled])');
      if (!item) {
        if (settled.current) setItemKey(null);
        return;
      }
      const items = [...node.querySelectorAll('.edl-select__item')];
      const key = String(items.indexOf(item));
      const next = { x: item.offsetLeft, y: item.offsetTop, w: item.offsetWidth, h: item.offsetHeight };
      setBox((prev) => (boxesEqual(prev, next) ? prev : next));
      if (settled.current) setItemKey((prev) => (prev === key ? prev : key));
    };
    place();
    arm.current = requestAnimationFrame(() => {
      settled.current = true;
      place();
    });
    const observer = new MutationObserver(place);
    observer.observe(node, { attributes: true, subtree: true, attributeFilter: ['data-highlighted', 'data-selected'] });
    listObserver.current = observer;
    const resize = new ResizeObserver(place);
    resize.observe(node);
    resizeObserver.current = resize;
  }, []);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner className="edl-select__positioner" side="bottom" align="start" sideOffset={6}>
        <SelectPrimitive.Popup className={['edl-select__popup', className].filter(Boolean).join(' ')} data-size={size}>
          <SelectPrimitive.List ref={listRef} className="edl-select__list">
            <span
              className="edl-select__highlight"
              data-shown={itemKey != null && box ? '' : undefined}
              data-move={glide ? '' : undefined}
              style={box ? { transform: `translate(${box.x}px, ${box.y}px)`, width: box.w, height: box.h } : undefined}
              aria-hidden="true"
            />
            {children}
          </SelectPrimitive.List>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({ value, children, disabled = false, icon: Icon }) {
  const size = useContext(SizeContext);
  const iconSize = size === 'compact' ? 14 : 16;
  return (
    <SelectPrimitive.Item value={value} disabled={disabled} className="edl-select__item" data-size={size}>
      {Icon ? <Icon size={iconSize} strokeWidth={1.5} aria-hidden="true" /> : null}
      <SelectPrimitive.ItemText className="edl-select__item-text">{children}</SelectPrimitive.ItemText>
      <span className="edl-select__check" aria-hidden="true">
        <SelectPrimitive.ItemIndicator>
          <Check size={iconSize} strokeWidth={2} />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function collectItems(children, out = []) {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props;
    if (child.type === SelectItem && typeof props.value === 'string') {
      out.push({ value: props.value, label: typeof props.children === 'string' ? props.children : props.value });
    } else if (props?.children) {
      collectItems(props.children, out);
    }
  });
  return out;
}
